/**
 * BillingScreen — desktop POS billing surface, gated by the day-open
 * billing session.
 *
 *  ┌────── SessionBar ──────────────────────────────────────────────┐
 *  │ Open since 09:14 · Cashier: Ravi · ₹X opening    [Close day]   │
 *  ├──────────────────────────────────────────┬─────────────────────┤
 *  │ SearchAddBar (search / scan → dropdown)   │ QuickAddPanel       │
 *  │ CartPanel  (central, roomy)               │  – popular / recent │
 *  │  – customer / held carts                  │    quick-add tiles  │
 *  │  – line items                             │                     │
 *  │  – totals + Charge                        │                     │
 *  └──────────────────────────────────────────┴─────────────────────┘
 *
 * If the active store has `requireDayOpen` and there's no OPEN session
 * (and the current user lacks an emergency-bill role), the entire surface
 * is replaced by {@link DayOpenScreen}. Once the day opens the cart
 * surface mounts and F9 / Charge swaps it for {@link CheckoutScreen}.
 *
 * Keyboard:
 *   /  Ctrl+K     focus search          (catalog)
 *   ↑↓←→ Enter   grid navigation       (catalog)
 *   F2           attach customer        (cart)
 *   F4           park current cart      (cart)
 *   F9           charge / complete sale (cart → checkout)
 */

import { useCallback, useEffect, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { FilePenLine, LayoutGrid, LockOpen, Receipt, X, PlusCircle } from 'lucide-react';
import Store from '@/db/models/Store';
import { database } from '@/db';
import { useAppStore } from '@/store/useAppStore';
import { getCartConfig, useCartStore } from '@/store/useCartStore';
import { useBillingSession } from '@/billing/useBillingSession';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { toBillingProduct } from '@/billing/toBillingProduct';
import { InvoiceSuccessDialog } from './InvoiceSuccessDialog';
import { SearchAddBar } from './SearchAddBar';
import { QuickAddPanel } from './QuickAddPanel';
import { CartPanel } from './CartPanel';
import { CustomerBar } from './CustomerBar';
import { CartActions } from './CartActions';
import { CheckoutScreen } from './CheckoutScreen';
import { ReceiptPreview } from './ReceiptPreview';
import { DayOpenScreen } from './DayOpenScreen';
// WEB ADAPTATION: stale-cache gate — no Desktop equivalent. See PLAN Decision 7.
import { StaleSyncScreen } from './StaleSyncScreen';
import { useStaleSyncGate } from '@/billing/useStaleSyncGate';
import { useSync } from '@/sync/useSync';
import { DayCloseDialog } from './DayCloseDialog';
import { ManualItemDialog } from './ManualItemDialog';
import type { RememberedManualItem } from '@/billing/manualItem';
import { useSettingsStore } from '@/store/useSettingsStore';
import { usePermissions, BILLING_QUICK_BILL } from '@/rbac';
import { QuickBillForm } from './QuickBillForm';
import { useQuickBillStore } from '@/store/useQuickBillStore';
import { addTypedItemsToCatalogue } from '@/billing/quickBillCatalogue';

type Mode = 'cart' | 'checkout';

export function BillingScreen() {
  // WEB ADAPTATION: stale-cache gate (PLAN Decision 7). No Desktop equivalent.
  const staleGate = useStaleSyncGate();
  const { triggerSync, isSyncing } = useSync();

  const [mode, setMode] = useState<Mode>('cart');
  const [showPreview, setShowPreview] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  // Phones: the quick-add panel has no room beside the cart, so it opens as a sheet.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [successInvoice, setSuccessInvoice] = useState<{
    id: string;
    number: string;
    total: number;
    customerPhone: string | null;
  } | null>(null);

  const itemCount = useCartStore((s) => s.itemCount);
  const addItem = useCartStore((s) => s.addItem);
  const clearPayments = useCartStore((s) => s.clearPayments);

  /**
   * Stable, because SearchAddBar's auto-add effect lists it as a dependency.
   * Passed inline, this screen handed down a new closure on every cart change
   * and re-triggered that effect mid-scan. SearchAddBar guards against the
   * resulting loop on its own now, but there is no reason to keep handing it a
   * moving target.
   */
  // Manual (non-catalog) lines, off unless this store allows them. When the
  // setting is off nothing is passed down and the search box behaves exactly
  // as it always has — there is no second path to reason about.
  const allowManualItems = useSettingsStore((s) => s.getBoolean('ALLOW_MANUAL_BILL_ITEMS'));
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSeed, setManualSeed] = useState('');
  const [manualPrefill, setManualPrefill] = useState<RememberedManualItem | null>(null);
  const isInterState = useCartStore((s) => s.isInterState);

  // Quick Bill: typed items + a hand-entered bill date (billing/billDate.ts).
  // Typed items are always on in this mode — that is what it is for — whatever
  // the store's ALLOW_MANUAL_BILL_ITEMS setting says for ordinary billing.
  const quickBill = useCartStore((s) => s.quickBill);
  const setQuickBill = useCartStore((s) => s.setQuickBill);
  const { can } = usePermissions();
  const canQuickBill = can(BILLING_QUICK_BILL);
  useEffect(() => {
    // A role change mid-session must not leave the mode on for someone who
    // may not use it. The writer refuses it anyway; this keeps the UI honest.
    if (quickBill && !canQuickBill) setQuickBill(false);
  }, [quickBill, canQuickBill, setQuickBill]);

  const openManual = useCallback((initialName: string, prefill?: RememberedManualItem) => {
    setManualSeed(initialName);
    setManualPrefill(prefill ?? null);
    setManualOpen(true);
  }, []);

  const handleAdd = useCallback(
    (p: Parameters<typeof toBillingProduct>[0], qty?: number) => addItem(toBillingProduct(p), qty),
    [addItem],
  );

  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const currentUser = useAppStore((s) => s.currentUser);
  const { activeSession, isSessionOpen, isLoading: sessionLoading } = useBillingSession();

  // Store-level day-open settings — observed reactively so toggling the
  // setting propagates without a refresh.
  const [requireDayOpen, setRequireDayOpen] = useState(false);
  const [hasEmergencyRole, setHasEmergencyRole] = useState(false);
  useEffect(() => {
    if (!activeStoreId) return;
    const sub = database
      .get<Store>('stores')
      .query(Q.where('id', activeStoreId))
      .observe()
      .subscribe((rows) => {
        const store = rows[0];
        if (!store) return;
        setRequireDayOpen(!!store.requireDayOpen);
        const role = currentUser?.role || '';
        let allowed = false;
        if (store.emergencyBillRoles) {
          try {
            const list: string[] = JSON.parse(store.emergencyBillRoles);
            allowed = list.includes(role);
          } catch { /* ignore malformed list */ }
        }
        setHasEmergencyRole(allowed);
      });
    return () => sub.unsubscribe();
  }, [activeStoreId, currentUser?.role]);

  // F9 → enter checkout from cart mode
  useEffect(() => {
    // Quick Bill owns F9: it must validate the form and reload the cart first.
    // Handling it here too jumped to checkout with whatever the cart held —
    // after "Back to cart", the lines as they were BEFORE the form was edited.
    if (mode !== 'cart' || quickBill) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F9') {
        e.preventDefault();
        if (itemCount > 0) setMode('checkout');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [itemCount, mode, quickBill]);

  const handleBackToCart = () => {
    clearPayments();
    setMode('cart');
  };

  // ─── Gate logic ─────────────────────────────────────────────────
  // WEB ADAPTATION: refuse to bill from a cache too old to trust while
  // offline. Ordered BEFORE the day-open gate: opening the day writes a
  // billing session, and doing that against stale data is exactly what this
  // is meant to prevent.
  if (!staleGate.checking && staleGate.blocked) {
    return <StaleSyncScreen state={staleGate} onRetry={triggerSync} isSyncing={isSyncing} />;
  }

  // While the WDB observable resolves we render a thin loading state
  // instead of flashing the day-open card unnecessarily.
  if (sessionLoading) {
    return (
      <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
        Loading session…
      </div>
    );
  }

  const needsDayOpen = !isSessionOpen && requireDayOpen && !hasEmergencyRole;
  if (needsDayOpen) {
    return <DayOpenScreen />;
  }

  // ─── Main billing surface ───────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-3">
      {(isSessionOpen || hasEmergencyRole) && (
        <SessionBar
          activeSession={activeSession}
          isEmergency={!isSessionOpen && hasEmergencyRole}
          onCloseDay={() => setCloseOpen(true)}
        />
      )}

      <div className="min-h-0 flex-1">
        {mode === 'cart' && quickBill ? (
          // Quick Bill: the invoice-style form replaces the cart. It loads its
          // rows into the cart and hands over to the same checkout below.
          <QuickBillForm onContinue={() => setMode('checkout')} />
        ) : mode === 'cart' ? (
          // There is no standalone action row above the search any more: it
          // cost a full row of height to hold one button. "Receipt" moved onto
          // the search row, which gives that height back to the cart — it could
          // previously show only three items.
          <div className="flex h-full flex-col">
            <div
              className={cn(
                'grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden md:gap-5',
                showPreview
                  ? 'md:grid-cols-[minmax(0,1fr)_300px_340px]'
                  : 'md:grid-cols-[minmax(0,1fr)_300px]',
              )}
            >
              {/* Centre: one action row (search · customer · receipt), then cart */}
              <div className="flex min-h-0 flex-col gap-3">
                {/* Wraps, and the scan box keeps a floor.
                 *
                 * At 1024 the customer control, cart actions and receipt toggle
                 * took their fixed widths first and the scan field — `flex-1`
                 * with no minimum — collapsed to about the width of its own
                 * search icon. That is the one control the whole till depends
                 * on, so it gets a floor and the rest of the row moves down
                 * instead. */}
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <div className="min-w-0 basis-full sm:min-w-[20rem] sm:basis-auto sm:flex-1">
                    <SearchAddBar
                      onAdd={handleAdd}
                      onAddManual={allowManualItems ? openManual : undefined}
                    />
                  </div>
                  <CustomerBar />
                  <CartActions />
                  {canQuickBill && !quickBill && (
                    <button
                      type="button"
                      onClick={() => setQuickBill(true)}
                      disabled={itemCount > 0}
                      title={itemCount > 0
                        ? 'Finish or park (F4) the current bill first — Quick Bill starts from an empty bill'
                        : 'Quick Bill — fill in an invoice by hand, any date'}
                      className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-stroke-hairline/15 px-3 text-label text-fg-secondary transition-colors duration-120 hover:bg-bg-subtle hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FilePenLine size={15} />
                      Quick Bill
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setQuickAddOpen(true)}
                    className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-stroke-hairline/15 px-3 text-label text-fg-secondary transition-colors duration-120 hover:bg-bg-subtle hover:text-fg-primary md:hidden"
                  >
                    <LayoutGrid size={15} />
                    Quick add
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    title={showPreview ? 'Hide receipt preview' : 'Show receipt preview'}
                    className={cn(
                      'hidden h-11 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-label transition-colors duration-120 md:inline-flex',
                      showPreview
                        ? 'border-accent/40 bg-accent/10 text-accent-ink'
                        : 'border-stroke-hairline/15 text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
                    )}
                  >
                    <Receipt size={15} />
                    Receipt
                  </button>
                </div>
                <div className="min-h-0 flex-1">
                  <CartPanel onCharge={() => setMode('checkout')} />
                </div>
              </div>
              {/* Right: quick add — recent / popular products (a sheet on phones) */}
              <div className="hidden min-h-0 md:flex md:flex-col">
                <QuickAddPanel onAdd={handleAdd} />
              </div>
              {showPreview && <div className="hidden min-h-0 md:block"><ReceiptPreview /></div>}
            </div>
          </div>
        ) : (
          <CheckoutScreen
            onBack={handleBackToCart}
            onComplete={(result) => {
              // Quick Bill: items typed on it join the catalogue now that the
              // bill is saved (not before — an abandoned bill leaves nothing).
              if (useCartStore.getState().quickBill && activeStoreId) {
                const draft = useQuickBillStore.getState();
                const rows = draft.rows;
                const ratesIncludeGst = draft.ratesIncludeGst;
                draft.reset();
                void addTypedItemsToCatalogue(activeStoreId, rows, { ratesIncludeGst, config: getCartConfig() })
                  .catch((err) => console.warn('[QuickBill] could not add typed items to the catalogue', err));
              }
              setMode('cart');
              setSuccessInvoice({
                id: result.invoiceId,
                number: result.invoiceNumber,
                total: result.total,
                customerPhone: result.customerPhone,
              });
            }}
          />
        )}
      </div>

      {/* Phones: quick add as a sheet. Stays open so several items can be tapped in. */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent title="Quick add" className="w-[calc(100vw-1.5rem)] max-w-md">
          <div className="flex h-[70vh] flex-col gap-3 p-3">
            <div className="min-h-0 flex-1">
              <QuickAddPanel onAdd={handleAdd} />
            </div>
            <Button block onClick={() => setQuickAddOpen(false)}>Done · {itemCount} {itemCount === 1 ? 'item' : 'items'} in cart</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day-close dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent title="Close the day" className="w-[460px]">
          <DayCloseDialog onClose={() => setCloseOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <Dialog open={!!successInvoice} onOpenChange={(o) => !o && setSuccessInvoice(null)}>
        <DialogContent title="Sale complete" className="w-[440px]">
          {successInvoice && (
            <InvoiceSuccessDialog
              invoiceId={successInvoice.id}
              invoiceNumber={successInvoice.number}
              totalAmount={successInvoice.total}
              customerPhone={successInvoice.customerPhone}
              onClose={() => setSuccessInvoice(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Non-catalog line */}
      <ManualItemDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        isInterState={isInterState}
        initialName={manualSeed}
        prefill={manualPrefill}
        onAdd={(product, qty) => addItem(product, qty)}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Session bar
// ═════════════════════════════════════════════════════════════════════

function SessionBar({
  activeSession,
  isEmergency,
  onCloseDay,
}: {
  activeSession: ReturnType<typeof useBillingSession>['activeSession'];
  isEmergency: boolean;
  onCloseDay: () => void;
}) {
  if (isEmergency) {
    return (
      <div className="flex items-center justify-between rounded-md border border-warning/40 bg-warning/10 px-4 py-2">
        <div className="flex items-center gap-2 text-body text-warning-ink">
          <LockOpen size={14} />
          <span>
            Emergency billing — the day is not open. Sales will be flagged for review.
          </span>
        </div>
      </div>
    );
  }

  if (!activeSession) return null;

  const opened = new Date(activeSession.openedAt).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 py-2 sm:px-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body">
        <span className="inline-flex h-2 w-2 rounded-full bg-success" aria-hidden />
        <span className="text-fg-primary">Day open</span>
        <span className="text-fg-tertiary">·</span>
        <span className="text-fg-secondary">since {opened}</span>
        <span className="text-fg-tertiary">·</span>
        <span className="text-fg-secondary tabular-nums">
          opening {formatCurrency(activeSession.openingCash)}
        </span>
      </div>
      <Button variant="ghost" size="sm" onClick={onCloseDay}>
        <X size={14} className="mr-1" /> Close day
      </Button>
    </div>
  );
}
